import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Copy, Download, HardDrive, KeyRound, Loader2, RefreshCw, XCircle } from "lucide-react";
import { api, errMsg, fmtBytes, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const psScript = (base, token) => `# Sauvegarde SDR PNEUS 91 - a planifier chaque nuit (Planificateur de taches Windows, 3h00)
$Base  = "${base}/api/backup"
$Token = "${token}"
$Dest  = "D:\\Sauvegardes\\SDR-PNEUS-91"
New-Item -ItemType Directory -Force -Path $Dest | Out-Null
$H = @{ Authorization = "Bearer $Token" }
$List = Invoke-RestMethod -Uri "$Base/list" -Headers $H
foreach ($B in $List) {
  $Out = Join-Path $Dest $B.filename
  if (Test-Path $Out) { continue }
  $L = Invoke-RestMethod -Uri "$Base/url/$($B.id)" -Headers $H
  if ($L.url) { Invoke-WebRequest -Uri $L.url -OutFile $Out -UseBasicParsing }
  else { Invoke-WebRequest -Uri "$Base/download/$($B.id)" -Headers $H -OutFile $Out -UseBasicParsing }
  $Hash = (Get-FileHash $Out -Algorithm SHA256).Hash.ToLower()
  if ($Hash -ne $B.sha256) { Remove-Item $Out; Write-Error "Empreinte invalide : $($B.filename)" }
  else { Write-Output "OK $($B.filename)" }
}`;

const shScript = (base, token) => `#!/bin/sh
# Sauvegarde SDR PNEUS 91 - crontab : 0 3 * * * /opt/sdr/sauvegarde.sh
BASE="${base}/api/backup"
TOKEN="${token}"
DEST="/srv/sauvegardes/sdr-pneus-91"
mkdir -p "$DEST"
curl -sf -H "Authorization: Bearer $TOKEN" "$BASE/list" \\
 | python3 -c 'import sys,json;[print(b["id"],b["filename"],b["sha256"]) for b in json.load(sys.stdin)]' \\
 | while read ID NAME SHA; do
  [ -f "$DEST/$NAME" ] && continue
  URL=$(curl -sf -H "Authorization: Bearer $TOKEN" "$BASE/url/$ID" | python3 -c 'import sys,json;print(json.load(sys.stdin).get("url") or "")')
  if [ -n "$URL" ]; then curl -sf -o "$DEST/$NAME" "$URL"
  else curl -sf -H "Authorization: Bearer $TOKEN" -o "$DEST/$NAME" "$BASE/download/$ID"; fi
  echo "$SHA  $DEST/$NAME" | sha256sum -c - || rm -f "$DEST/$NAME"
done`;

function TokenDialog({ token, onClose }) {
  const base = window.location.origin;
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copié"); };
  return (
    <Dialog open={!!token} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="backup-token-dialog">
        <DialogHeader>
          <DialogTitle>Récupération sur votre serveur</DialogTitle>
          <DialogDescription>Cette clé ne sera plus affichée. Copiez le script sur votre serveur et planifiez-le chaque nuit : il télécharge chaque nouvelle sauvegarde et vérifie son empreinte. Rien n'est jamais supprimé sur votre serveur.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 bg-slate-100 rounded-md p-2 text-xs font-mono break-all" data-testid="backup-token-value">
          <KeyRound className="h-4 w-4 shrink-0" />{token}
          <Button size="icon" variant="ghost" className="ml-auto shrink-0" onClick={() => copy(token)} data-testid="backup-token-copy"><Copy className="h-4 w-4" /></Button>
        </div>
        <Tabs defaultValue="ps">
          <TabsList><TabsTrigger value="ps" data-testid="backup-script-ps-tab">Windows (PowerShell)</TabsTrigger><TabsTrigger value="sh" data-testid="backup-script-sh-tab">Linux / NAS</TabsTrigger></TabsList>
          {[["ps", psScript(base, token)], ["sh", shScript(base, token)]].map(([k, script]) => (
            <TabsContent key={k} value={k}>
              <pre className="bg-[#0F172A] text-slate-100 text-[11px] p-3 rounded-md max-h-72 overflow-auto whitespace-pre" data-testid={`backup-script-${k}`}>{script}</pre>
              <Button size="sm" variant="outline" className="mt-2" onClick={() => copy(script)} data-testid={`backup-script-${k}-copy`}><Copy className="h-3.5 w-3.5 mr-1" />Copier le script</Button>
            </TabsContent>
          ))}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

export function AdminBackups() {
  const [data, setData] = useState(null);
  const [token, setToken] = useState("");
  const load = () => api.get("/admin/backups").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const running = data?.backups.some((b) => b.status === "running");
  useEffect(() => { if (!running) return undefined; const t = setInterval(load, 4000); return () => clearInterval(t); }, [running]);
  const run = async (full) => {
    try { await api.post("/admin/backups/run", { full }); toast.success(full ? "Sauvegarde complète lancée (peut durer plusieurs heures)" : "Sauvegarde lancée"); setTimeout(load, 800); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const genToken = async () => { const { data: d } = await api.post("/admin/backups/token"); setToken(d.token); load(); };
  const download = async (b) => {
    const t = toast.loading("Téléchargement…");
    try {
      const { data: l } = await api.get(`/admin/backups/${b.id}/link`);
      if (l.url) { window.location.href = l.url; toast.success("Téléchargement démarré", { id: t }); return; }
      const r = await api.get(`/admin/backups/${b.id}/download`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = b.filename; a.click();
      setTimeout(() => URL.revokeObjectURL(u), 3000); toast.success("Sauvegarde téléchargée", { id: t });
    } catch (e) { toast.error(errMsg(e), { id: t }); }
  };
  if (!data) return <Loader2 className="h-5 w-5 animate-spin" />;
  return (
    <div className="space-y-6" data-testid="admin-backups">
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border rounded-xl p-5 space-y-3">
          <h3 className="font-display font-bold flex items-center gap-2"><HardDrive className="h-4 w-4 text-[#1E3A8A]" />Sauvegarde automatique</h3>
          <p className="text-sm text-slate-600">Chaque nuit à 2h : dossiers, droits, commentaires, validations et journal d'audit. Chaque dimanche : sauvegarde complète avec tous les documents, construite directement dans votre stockage OVH sans limite de taille. Les {data.full_keep} dernières sauvegardes complètes sont conservées. Chaque archive est vérifiée (empreintes et relecture) ; en cas d'échec, les administrateurs reçoivent un email.</p>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => run(false)} disabled={running} data-testid="backup-run-btn">{running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}Sauvegarder les métadonnées</Button>
            <Button variant="outline" onClick={() => run(true)} disabled={running} data-testid="backup-run-full-btn"><HardDrive className="h-4 w-4 mr-2" />Sauvegarde complète maintenant</Button>
          </div>
        </div>
        <div className="bg-white border rounded-xl p-5 space-y-3">
          <h3 className="font-display font-bold flex items-center gap-2"><KeyRound className="h-4 w-4 text-[#1E3A8A]" />Copie sur votre propre serveur</h3>
          <p className="text-sm text-slate-600">Votre serveur vient chercher chaque nuit les nouvelles sauvegardes avec une clé secrète. Aucun accès entrant à votre serveur n'est nécessaire.</p>
          <p className="text-xs font-semibold" data-testid="backup-token-status">{data.token_configured ? `Clé active depuis le ${fmtDate(data.token_created_at)}` : "Aucune clé créée"}</p>
          <Button variant="outline" onClick={genToken} data-testid="backup-token-btn">{data.token_configured ? "Générer une nouvelle clé (l'ancienne cesse de fonctionner)" : "Créer la clé et le script"}</Button>
        </div>
      </div>
      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b font-display font-bold text-sm">Historique des sauvegardes</div>
        {data.backups.length === 0 && <p className="p-6 text-sm text-slate-500">Aucune sauvegarde pour le moment.</p>}
        <div className="divide-y">
          {data.backups.map((b) => (
            <div key={b.id} className="px-4 py-3 flex flex-wrap items-center gap-3 text-sm" data-testid={`backup-row-${b.id}`}>
              {b.status === "running" ? <Loader2 className="h-4 w-4 animate-spin text-[#1E3A8A]" />
                : b.status === "done" && b.restore_test?.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-red-600" />}
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate flex items-center gap-2">{b.filename}
                  <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${b.metadata_only ? "bg-slate-100 text-slate-600" : "bg-blue-100 text-[#1E3A8A]"}`} data-testid={`backup-kind-${b.id}`}>{b.metadata_only ? "Métadonnées" : "Complète"}</span>
                </div>
                <div className="text-xs text-slate-500">
                  {fmtDate(b.created_at)} · {b.trigger === "manuel" ? "manuelle" : "automatique"}
                  {b.status === "done" && <> · {b.files} fichiers · {fmtBytes(b.size)}</>}
                  {b.status === "failed" && <> · échec ({b.error})</>}
                </div>
              </div>
              {b.status === "done" && (
                <span className={`text-xs font-semibold ${b.restore_test?.ok ? "text-emerald-700" : "text-red-700"}`} data-testid={`backup-restore-${b.id}`}>
                  {b.restore_test?.ok ? (b.metadata_only ? "Archive vérifiée" : `Vérifiée : ${b.restore_test.checked} fichiers intacts`) : `Test échoué : ${b.restore_test?.errors?.[0] || ""}`}
                </span>
              )}
              <span className="text-xs text-slate-500">{b.downloaded_at ? `Récupérée le ${fmtDate(b.downloaded_at)}` : b.status === "done" ? "Pas encore récupérée" : ""}</span>
              {b.status === "done" && <Button size="sm" variant="ghost" onClick={() => download(b)} data-testid={`backup-download-${b.id}`}><Download className="h-4 w-4" /></Button>}
            </div>
          ))}
        </div>
      </div>
      <TokenDialog token={token} onClose={() => setToken("")} />
    </div>
  );
}
